import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { themes } from '../themes/tokens';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'dark'
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  // Initialize glass preference
  const [glassEnabled, setGlassEnabled] = useState(() => {
    const saved = localStorage.getItem('glassEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const theme = useMemo(() => themes[currentTheme] || themes.dark, [currentTheme]);

  const toggleTheme = useMemo(() => () => {
    setCurrentTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const toggleGlass = useMemo(() => () => {
    setGlassEnabled(prev => !prev);
  }, []);

  // Apply theme to CSS variables and persist to localStorage
  useEffect(() => {
    if (!theme || !theme.colors) {
      console.error('Theme not properly loaded:', currentTheme);
      return;
    }

    const root = document.documentElement;
    const colors = theme.colors;

    // Update CSS variables
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-dark', colors.primaryDark);
    root.style.setProperty('--color-primary-light', colors.primaryLight);

    // Glassmorphism logic
    if (glassEnabled) {
      // Glass ON: Use defined glass tokens
      if (colors.glass) {
        root.style.setProperty('--glass-background', colors.glass.background);
        root.style.setProperty('--glass-border', colors.glass.border);
        root.style.setProperty('--glass-text', colors.glass.text);
        root.style.setProperty('--glass-blur', colors.glass.blur);
      }
      if (colors.glassHigh) {
        root.style.setProperty('--glass-high-background', colors.glassHigh.background);
        root.style.setProperty('--glass-high-blur', colors.glassHigh.blur);
      }
      // Button transparency for glass effect
      root.style.setProperty('--button-opacity-primary', '0.85');
      root.style.setProperty('--button-blur', '8px');
    } else {
      // Glass OFF: Use solid opaque colors
      root.style.setProperty('--glass-background', colors.surface); // Solid surface
      root.style.setProperty('--glass-border', colors.border);
      root.style.setProperty('--glass-text', colors.textPrimary);
      root.style.setProperty('--glass-blur', '0px');

      root.style.setProperty('--glass-high-background', colors.surfaceLight);
      root.style.setProperty('--glass-high-blur', '0px');

      // Button solid for opaque effect
      root.style.setProperty('--button-opacity-primary', '1');
      root.style.setProperty('--button-blur', '0px');
    }

    // Helper to extract RGB for transparency
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
    };

    const primaryRgb = hexToRgb(colors.primary);
    if (primaryRgb) root.style.setProperty('--color-primary-rgb', primaryRgb);

    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-background-light', colors.surface);
    root.style.setProperty('--color-background-lighter', colors.surfaceLight);

    // Status RGBs
    const successRgb = hexToRgb(colors.success);
    const errorRgb = hexToRgb(colors.error);
    if (successRgb) root.style.setProperty('--color-success-rgb', successRgb);
    if (errorRgb) root.style.setProperty('--color-error-rgb', errorRgb);

    root.style.setProperty('--color-text', colors.textPrimary);
    root.style.setProperty('--color-text-primary', colors.textPrimary);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-text-tertiary', colors.textTertiary);
    root.style.setProperty('--color-text-disabled', colors.textDisabled);

    root.style.setProperty('--color-input-border', colors.inputBorder);
    root.style.setProperty('--color-input-border-focus', colors.primary);
    root.style.setProperty('--color-input-background', colors.inputBackground);
    root.style.setProperty('--color-input-text', colors.inputText);
    root.style.setProperty('--color-label', colors.textPrimary);

    // Status colors with variants
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-success-dark', colors.successDark);
    root.style.setProperty('--color-success-light', colors.successLight);
    root.style.setProperty('--color-success-background', colors.successLight);

    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-error-dark', colors.errorDark);
    root.style.setProperty('--color-error-light', colors.errorLight);
    root.style.setProperty('--color-error-background', colors.errorLight);

    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-warning-dark', colors.warningDark);
    root.style.setProperty('--color-warning-light', colors.warningLight);
    root.style.setProperty('--color-warning-background', colors.warningLight);

    root.style.setProperty('--color-info', colors.info);
    root.style.setProperty('--color-info-dark', colors.infoDark);
    root.style.setProperty('--color-info-light', colors.infoLight);
    root.style.setProperty('--color-info-background', colors.infoLight);

    // Border colors
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-border-light', colors.borderLight);
    root.style.setProperty('--color-border-dark', colors.borderDark);

    // Role colors
    root.style.setProperty('--color-role-admin', colors.roleAdmin);
    root.style.setProperty('--color-role-admin-light', colors.roleAdminLight);
    root.style.setProperty('--color-role-club-admin', colors.roleClubAdmin);
    root.style.setProperty('--color-role-club-admin-light', colors.roleClubAdminLight);
    root.style.setProperty('--color-role-coach', colors.roleCoach);
    root.style.setProperty('--color-role-coach-light', colors.roleCoachLight);
    root.style.setProperty('--color-role-player', colors.rolePlayer);
    root.style.setProperty('--color-role-player-light', colors.rolePlayerLight);

    // Surface levels
    root.style.setProperty('--surface-lvl0', colors.surfaceLvl0 || colors.background);
    root.style.setProperty('--surface-lvl1', colors.surfaceLvl1 || colors.surface);
    root.style.setProperty('--surface-lvl2', colors.surfaceLvl2 || colors.surfaceLight);
    root.style.setProperty('--surface-lvl3', colors.surfaceLvl3 || colors.surfaceLighter);

    // Specialty colors
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-accent-light', colors.accentLight);

    // Brand icon colors (for WorldsPitch logo/icon)
    root.style.setProperty('--color-brand-icon', colors.brandIcon);
    root.style.setProperty('--color-brand-icon-glow', colors.brandIconGlow);

    // Action colors - Semantic button actions
    root.style.setProperty('--color-action-view', colors.actionView);
    root.style.setProperty('--color-action-view-hover', colors.actionViewHover);
    root.style.setProperty('--color-action-view-light', colors.actionViewLight);

    root.style.setProperty('--color-action-edit', colors.actionEdit);
    root.style.setProperty('--color-action-edit-hover', colors.actionEditHover);
    root.style.setProperty('--color-action-edit-light', colors.actionEditLight);

    root.style.setProperty('--color-action-delete', colors.actionDelete);
    root.style.setProperty('--color-action-delete-hover', colors.actionDeleteHover);
    root.style.setProperty('--color-action-delete-light', colors.actionDeleteLight);

    root.style.setProperty('--color-action-save', colors.actionSave);
    root.style.setProperty('--color-action-save-hover', colors.actionSaveHover);
    root.style.setProperty('--color-action-save-light', colors.actionSaveLight);

    root.style.setProperty('--color-action-cancel', colors.actionCancel);
    root.style.setProperty('--color-action-cancel-hover', colors.actionCancelHover);
    root.style.setProperty('--color-action-cancel-light', colors.actionCancelLight);

    root.style.setProperty('--color-action-warning', colors.actionWarning);
    root.style.setProperty('--color-action-warning-hover', colors.actionWarningHover);
    root.style.setProperty('--color-action-warning-light', colors.actionWarningLight);

    // Interactive states
    root.style.setProperty('--color-hover', colors.hover);
    root.style.setProperty('--color-focus-ring', colors.focus);
    root.style.setProperty('--color-active', colors.active);
    root.style.setProperty('--color-disabled', colors.disabled);
    root.style.setProperty('--color-disabled-text', colors.disabledText);

    // Overlays
    root.style.setProperty('--color-overlay', colors.overlay);
    root.style.setProperty('--color-overlay-light', colors.overlayLight);
    root.style.setProperty('--color-overlay-medium', colors.overlayMedium);
    root.style.setProperty('--color-overlay-heavy', colors.overlayHeavy);

    // Component backgrounds
    root.style.setProperty('--color-primary-background', colors.primaryBackground);
    root.style.setProperty('--color-footer-background', colors.footerBackground);
    root.style.setProperty('--color-page-gradient', colors.pageGradient);
    root.style.setProperty('--color-card', colors.surface);
    root.style.setProperty('--color-modal-backdrop', colors.modalBackdrop);

    // Legacy variable mappings for backward compatibility
    root.style.setProperty('--primary-black', colors.background);
    root.style.setProperty('--primary-white', colors.textPrimary);
    root.style.setProperty('--primary-green', colors.primary);
    root.style.setProperty('--secondary-gray', colors.textTertiary);
    root.style.setProperty('--template-dark', colors.surface);

    // Add theme class to body for theme-specific styles
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${currentTheme}`);
    if (glassEnabled) {
      document.body.classList.add('glass-enabled');
      document.body.classList.remove('glass-disabled');
    } else {
      document.body.classList.add('glass-disabled');
      document.body.classList.remove('glass-enabled');
    }

    // Persist theme preference
    localStorage.setItem('theme', currentTheme);
    localStorage.setItem('glassEnabled', glassEnabled);
  }, [currentTheme, theme, glassEnabled]);

  const contextValue = useMemo(() => ({
    theme,
    currentTheme,
    toggleTheme,
    glassEnabled,
    toggleGlass
  }), [theme, currentTheme, toggleTheme, glassEnabled, toggleGlass]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};